/**
 * Per-project meta (safety + 3D asset state) — local, separate from Studio BOM shape.
 */

const META_KEY = "prometheus_project_meta_v1";

export type RiskClass = "low" | "electrical" | "structural" | "chemical" | "high_power";

export type Asset3dState = "didactico" | "importado" | "aprobado";

export type ProjectMeta = {
  riskClass: RiskClass;
  /** checklist item id → done */
  safetyDone: Record<string, boolean>;
  asset3d: {
    state: Asset3dState;
    note?: string;
    source?: string;
    updatedAt: string;
  };
  updatedAt: string;
};

export type SafetyCheckItem = {
  id: string;
  label: string;
  detail: string;
  /** If true, blocking for Monitor/USB soft-gate */
  critical?: boolean;
};

const RISK_LABEL: Record<RiskClass, string> = {
  low: "Bajo (mecánico / PLA-PETG)",
  electrical: "Eléctrico (MCU · 5–24V)",
  structural: "Estructural / ASCE 7",
  chemical: "Químico / solventes",
  high_power: "Alta potencia (AC / LiFePO4)",
};

export function riskClassLabel(rc: RiskClass): string {
  return RISK_LABEL[rc];
}

/** Infer risk from project title/discipline when unset. */
export function inferRiskClass(title: string, discipline?: string): RiskClass {
  const hay = `${title} ${discipline || ""}`.toLowerCase();
  if (/solar|lifepo4|luma|inversor|48v|bater/.test(hay)) return "high_power";
  if (/casa|concreto|asce|viga|zapata|hurac|techo/.test(hay)) return "structural";
  if (/quim|acido|soldadur|flux|epoxi/.test(hay)) return "chemical";
  if (/esp32|arduino|relay|wiring|gabinete|ip67|mcu|i2c/.test(hay)) return "electrical";
  return "low";
}

export function safetyChecklistFor(risk: RiskClass): SafetyCheckItem[] {
  const base: SafetyCheckItem[] = [
    {
      id: "eye-ppe",
      label: "PPE básico",
      detail: "Gafas / zona despejada / ventilación si hay polvo o humo.",
    },
    {
      id: "power-off",
      label: "Energía cortada al cablear",
      detail: "Fuente/USB desconectados antes de cambiar pines o soldar.",
      critical: true,
    },
  ];

  if (risk === "electrical") {
    return [
      ...base,
      {
        id: "gnd-common",
        label: "GND común verificado",
        detail: "MCU, Buck y cargas comparten tierra — sin bucles sucios al ADC.",
        critical: true,
      },
      {
        id: "no-12v-gpio",
        label: "Sin 12V/24V a GPIO",
        detail: "Señal lógica al módulo; potencia al lado de carga.",
        critical: true,
      },
      {
        id: "fuse-positive",
        label: "Fusible en +V",
        detail: "Fusible cercano a la fuente / banco, cable dimensionado.",
        critical: true,
      },
      {
        id: "polaridad",
        label: "Polaridad marcada",
        detail: "Rojo/negro o conectores keyed — evita invertir DC.",
        critical: true,
      },
      {
        id: "conformal-pr",
        label: "Humedad / salitre considerado",
        detail: "Conformal o gabinete sellado si hay costa / condensación.",
      },
    ];
  }

  if (risk === "structural") {
    return [
      ...base,
      {
        id: "asce-wind",
        label: "Carga viento 170+ mph",
        detail: "Anclaje a estructura real (ASCE 7 Cat 5), no solo lamina floja.",
        critical: true,
      },
      {
        id: "inox-hardware",
        label: "Hardware anti-salitre",
        detail: "Inox 316 o galvanizado — no tornillo negro de drywall en exterior.",
        critical: true,
      },
      {
        id: "fos",
        label: "FoS revisado",
        detail: "FoS ≥ 2 (maker) / ≥ 3 si impacto o vuelco.",
      },
    ];
  }

  if (risk === "chemical") {
    return [
      ...base,
      {
        id: "vent",
        label: "Ventilación / extracción",
        detail: "Humos de soldadura, epoxy o solventes fuera del pecho.",
        critical: true,
      },
      {
        id: "ppe-chem",
        label: "Guantes / protección química",
        detail: "Según MSDS del producto.",
        critical: true,
      },
      {
        id: "storage",
        label: "Almacenamiento cerrado",
        detail: "Tapas, lejos de calor y niños/mascotas.",
      },
    ];
  }

  if (risk === "high_power") {
    return [
      ...base,
      {
        id: "bms-intact",
        label: "BMS intacto (no puenteado)",
        detail: "Nunca bypassear BMS «solo un minuto».",
        critical: true,
      },
      {
        id: "class-t",
        label: "Fusible DC / Class T si aplica",
        detail: "Protección de falla cerca del banco LiFePO4.",
        critical: true,
      },
      {
        id: "ac-isolation",
        label: "Aislamiento AC / transferencia",
        detail: "Subpanel crítico y transfer switch — no backfeed a LUMA.",
        critical: true,
      },
      {
        id: "gnd-common",
        label: "GND / bonding revisado",
        detail: "Tierra de chasis e inversor según manual.",
        critical: true,
      },
    ];
  }

  // low
  return [
    ...base,
    {
      id: "sharp-edges",
      label: "Bordes / herramientas",
      detail: "Cutter, sierra e impresora 3D: manos lejos de hotend/bed caliente.",
    },
    {
      id: "print-area",
      label: "Área de impresión estable",
      detail: "Cama limpia, pieza no se despega hacia la boquilla.",
    },
  ];
}

type MetaMap = Record<string, ProjectMeta>;

function readMap(): MetaMap {
  try {
    const raw = localStorage.getItem(META_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as MetaMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMap(map: MetaMap) {
  try {
    localStorage.setItem(META_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event("prometheus-project-meta-changed"));
}

function defaultMeta(title: string, discipline?: string): ProjectMeta {
  const now = new Date().toISOString();
  return {
    riskClass: inferRiskClass(title, discipline),
    safetyDone: {},
    asset3d: { state: "didactico", updatedAt: now },
    updatedAt: now,
  };
}

export function getProjectMeta(
  projectId: string,
  title?: string,
  discipline?: string
): ProjectMeta {
  const map = readMap();
  if (map[projectId]) return map[projectId];
  return defaultMeta(title || projectId, discipline);
}

export function saveProjectMeta(projectId: string, meta: ProjectMeta) {
  const map = readMap();
  map[projectId] = { ...meta, updatedAt: new Date().toISOString() };
  writeMap(map);
}

export function setRiskClass(
  projectId: string,
  riskClass: RiskClass,
  title?: string,
  discipline?: string
): ProjectMeta {
  const meta = getProjectMeta(projectId, title, discipline);
  const next = { ...meta, riskClass, updatedAt: new Date().toISOString() };
  saveProjectMeta(projectId, next);
  return next;
}

export function toggleSafetyItem(
  projectId: string,
  itemId: string,
  title?: string,
  discipline?: string
): ProjectMeta {
  const meta = getProjectMeta(projectId, title, discipline);
  const safetyDone = { ...meta.safetyDone, [itemId]: !meta.safetyDone[itemId] };
  const next = { ...meta, safetyDone, updatedAt: new Date().toISOString() };
  saveProjectMeta(projectId, next);
  return next;
}

export function setAsset3dState(
  projectId: string,
  state: Asset3dState,
  opts?: { note?: string; source?: string; title?: string; discipline?: string }
): ProjectMeta {
  const meta = getProjectMeta(projectId, opts?.title, opts?.discipline);
  const next: ProjectMeta = {
    ...meta,
    asset3d: {
      state,
      note: opts?.note ?? meta.asset3d.note,
      source: opts?.source ?? meta.asset3d.source,
      updatedAt: new Date().toISOString(),
    },
    updatedAt: new Date().toISOString(),
  };
  saveProjectMeta(projectId, next);
  return next;
}

export function safetyProgress(
  projectId: string,
  title?: string,
  discipline?: string
): { done: number; total: number; criticalMissing: SafetyCheckItem[]; allCriticalOk: boolean } {
  const meta = getProjectMeta(projectId, title, discipline);
  const items = safetyChecklistFor(meta.riskClass);
  const done = items.filter((i) => meta.safetyDone[i.id]).length;
  const criticalMissing = items.filter((i) => i.critical && !meta.safetyDone[i.id]);
  return {
    done,
    total: items.length,
    criticalMissing,
    allCriticalOk: criticalMissing.length === 0,
  };
}

export const ASSET3D_LABEL: Record<Asset3dState, string> = {
  didactico: "Didáctico",
  importado: "Importado",
  aprobado: "Aprobado",
};

export function buildSafetyContextBlock(
  projectId: string,
  title?: string,
  discipline?: string
): string {
  const meta = getProjectMeta(projectId, title, discipline);
  const prog = safetyProgress(projectId, title, discipline);
  const missing = prog.criticalMissing.map((i) => i.label).join("; ");
  return `[SEGURIDAD DEL PROYECTO · risk_class=${meta.riskClass} (${riskClassLabel(meta.riskClass)})]:
Checklist ${prog.done}/${prog.total}. Críticos pendientes: ${missing || "ninguno"}.
Asset 3D: ${ASSET3D_LABEL[meta.asset3d.state]}${meta.asset3d.note ? ` — ${meta.asset3d.note}` : ""}.
DIRECTIVA: No indiques flashear, energizar 12V+ ni marcar Monitor online si faltan ítems críticos; pide completar Seguridad en Artifact.`;
}
